import DOMPurify from 'dompurify';
import { linkifyHtml } from './utils/linkify';

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GeneratorForm } from './components/GeneratorForm';
import { NameList } from './components/NameList';
import { UpgradePro } from './components/UpgradePro';
import { PdfIcon } from './components/icons/PdfIcon';
import { generateBrandNames } from './services/nameGenerationService';
import { exportToPdf } from './services/pdfService';
import * as authService from './services/authService';
import type { UserInputs, GeneratedName, PageContent, ProPlan } from './types';
import { MAX_FREE_GENERATIONS } from './constants';
import { useAuth } from './hooks/useAuth';
import { useContent } from './hooks/useContent';
import { useSeo } from './hooks/useSeo';
import { AdminControls } from './components/AdminControls';
import { defaultContent } from './contexts/ContentContext';
import { initGA, trackPageview } from './src/gtag';
import { useToast } from './hooks/useToast';

const App: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser, setModalView } = useAuth();
  const { content: allContent, isLoaded, isAdminMode, updatePageContent } = useContent();
  const { showToast } = useToast();
  
  // --- GA4 Integration Start ---
  useEffect(() => {
    if (!navigator.userAgent.includes('ReactSnap')) {
      initGA();
    }
  }, []);

  useEffect(() => {
    if (!navigator.userAgent.includes('ReactSnap')) {
      trackPageview(location.pathname + location.search);
    }
  }, [location]);
  // --- GA4 Integration End ---
  
  // ✅ Initialize pageContent directly from context, which has defaults.
  // This state is needed for the AdminControls to work.
  const [pageContent, setPageContent] = useState<PageContent>(allContent.home);
  // ✅ isLoading is now directly from the context's isLoaded flag.
  const isLoadingContent = !isLoaded;

  // ✅ Sync local state if context changes (e.g., after an admin save).
  useEffect(() => {
    if (isLoaded) {
      setPageContent(allContent.home);
    }
  }, [isLoaded, allContent.home]);

  const currentSlug = window.location.pathname.replace(/^\/+|\/+$/g, '') || 'home';
  const onSeoApplied = useCallback(() => {
    window.dispatchEvent(new Event('app-rendered'));
  }, []);

  useSeo(pageContent, currentSlug, onSeoApplied);

  const [userInputs, setUserInputs] = useState<UserInputs | null>(null);
  const [generatedNames, setGeneratedNames] = useState<GeneratedName[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [generationCount, setGenerationCount] = useState<number>(0);
  const [isPro, setIsPro] = useState<boolean>(false);

  useEffect(() => {
    if (user) {
      setIsPro(user.isPro);
      // Use Firestore as the single source of truth for generation count
      setGenerationCount(user.stats?.generationsUsed || 0);
    } else {
      setIsPro(false);
      const count = sessionStorage.getItem('generationCount') || '0';
      setGenerationCount(parseInt(count, 10));
    }
  }, [user]);

  const isLimitReached = generationCount >= MAX_FREE_GENERATIONS && !isPro;

  const handleGenerate = useCallback(async (inputs: UserInputs) => {
    if (isLimitReached) {
      setError(`You have reached the free limit of ${MAX_FREE_GENERATIONS} generations.`);
      return;
    }

    setIsLoading(true);
    setError(null);
    setUserInputs(inputs);

    try {
      const names = generateBrandNames(inputs);
      setGeneratedNames(prevNames => [...names, ...prevNames]);
      
      const newCount = generationCount + 1;
      setGenerationCount(newCount);

      if (user) {
        await authService.addGeneratedNamesToHistory(user.uid, names, inputs);
        // Update both stats atomically, but only set user with the one that drives UI logic
        await authService.updateUserStat(user.uid, 'namesGenerated', names.length);
        const updatedUser = await authService.updateUserStat(user.uid, 'generationsUsed');
        if (updatedUser) {
          setUser(updatedUser);
        }
      } else {
        sessionStorage.setItem('generationCount', newCount.toString());
      }
    } catch (err) {
      console.error(err);
      setError('An error occurred while generating names. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [generationCount, isLimitReached, isPro, user, setUser]);
  
  const handleUpgradeSuccess = useCallback(async (plan: ProPlan) => {
    if (user) {
      // The most important step: save the Pro status to the database.
      const updatedUser = await authService.updateUserProStatus(user.uid, true, plan);
      if (updatedUser) {
        setUser(updatedUser); // Update the global user state.
      }
      // No need to interact with sessionStorage for logged-in users.
    }
  }, [user, setUser]);

  const handleDownloadPdf = useCallback(async () => {
    if (userInputs && generatedNames.length > 0) {
      await exportToPdf(generatedNames, "Brand Name Ideas", user?.isPro || false, user?.email, userInputs);
      if (user) {
        const updatedUser = await authService.updateUserStat(user.uid, 'pdfsExported');
        if (updatedUser) {
          setUser(updatedUser);
        }
      }
    }
  }, [generatedNames, userInputs, user, setUser]);
  
  const handleSave = async () => {
    if (pageContent) {
      const oldSlug = allContent.home?.seo?.slug;
      const redirectCreated = await updatePageContent('home', pageContent);
      showToast('Homepage content saved!');
      if (redirectCreated) {
        const newSlug = pageContent.seo.slug;
        showToast(`Redirect created: ${oldSlug} -> ${newSlug}`);
      }
    }
  };

  const handleToggleFavorite = async (name: GeneratedName, isCurrentlyFavorited: boolean) => {
    if (!user) {
      setModalView('signup');
      showToast('Please sign up to save names.');
      return;
    }
    try {
      const updatedUser = await authService.toggleFavoriteName(user.uid, name, isCurrentlyFavorited);
      if (updatedUser) {
        setUser(updatedUser);
        showToast(isCurrentlyFavorited ? 'Name removed from favorites.' : 'Name saved to favorites!');
      }
    } catch (error) {
      console.error("Failed to update favorites:", error);
      showToast('Could not update favorites. Please try again.');
    }
  };

  const sanitizedBody = pageContent?.body ? linkifyHtml(pageContent.body) : '';

  return (
    <>
      {isLoadingContent ? (
        <div className="flex items-center justify-center h-screen bg-white">
          <div className="flex items-center text-gray-600">
            <svg className="animate-spin h-8 w-8 text-[#4F46E5]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="ml-3 text-lg">Loading Content...</span>
          </div>
        </div>
      ) : (
        <div className="w-full max-w-6xl mx-auto py-12 md:py-20 px-4 sm:px-6 lg:px-8 text-[#1F2937]">
          <div className="w-full max-w-3xl mx-auto mb-12 text-center">
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 mb-6">
              {pageContent.h1}
            </h1>
            <div
              className="text-lg md:text-xl text-gray-600 leading-relaxed prose max-w-none mx-auto"
              dangerouslySetInnerHTML={{ __html: sanitizedBody }}
            />
          </div>
        
        {isAdminMode && (
            <AdminControls 
                pageId="home" 
                pageContent={pageContent} 
                onContentChange={setPageContent}
                onSave={handleSave}
            />
        )}

        <div className="w-full max-w-3xl mx-auto">
            <div className="bg-white shadow-2xl rounded-2xl p-8 md:p-10 border border-gray-100">
                <GeneratorForm onSubmit={handleGenerate} isLoading={isLoading} isLimitReached={isLimitReached} />
                
                <div className="text-center mt-4 text-sm text-[#6B7280]">
                {isPro ? (
                    <p>Unlimited generations (Pro User)</p>
                ) : (
                    !isLimitReached && (
                    <p>
                        Generations left: {Math.max(0, MAX_FREE_GENERATIONS - generationCount)}/{MAX_FREE_GENERATIONS}
                    </p>
                    )
                )}
                </div>

                {user && !isPro && MAX_FREE_GENERATIONS - generationCount <= 1 && !isLimitReached && (
                    <div className="text-center mt-2 p-3 rounded-md text-sm bg-indigo-50 border border-indigo-200">
                        <p className="text-indigo-700">You’re almost out of free generations — upgrade to Pro for unlimited access!</p>
                        <button
                            onClick={() => navigate('/dashboard/')}
                            className="mt-2 inline-block px-4 py-1.5 bg-[#4F46E5] hover:bg-[#6366F1] text-white font-semibold rounded-lg shadow-sm transition-colors duration-300 text-xs"
                        >
                            Upgrade to Pro
                        </button>
                    </div>
                )}

                {isLimitReached && !user && (
                <div className="mt-6 text-center bg-indigo-50 border-l-4 border-[#4F46E5] p-6 rounded-md">
                    <h3 className="font-bold text-lg text-indigo-700">Free Limit Reached</h3>
                    <p className="my-2 text-indigo-600">Sign up to continue generating unlimited names.</p>
                    <button
                        onClick={() => setModalView('signup')}
                        className="mt-4 inline-block px-6 py-2.5 bg-[#4F46E5] hover:bg-[#6366F1] text-white font-bold rounded-lg shadow-md transition-all duration-300"
                    >
                        Sign Up Now
                    </button>
                </div>
                )}
                {isLimitReached && user && (
                <UpgradePro onUpgradeSuccess={handleUpgradeSuccess} />
                )}
            </div>

            {error && !isLimitReached && (
                <div className="mt-6 text-center bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-md" role="alert">
                <p>{error}</p>
                </div>
            )}
        </div>

        {generatedNames.length > 0 && (
            <div className="mt-10 bg-white shadow-lg rounded-xl p-6 md:p-8 border border-gray-200 relative">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-[#1F2937]">Generated Names</h2>
                <button
                onClick={handleDownloadPdf}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-[#1F2937] font-semibold rounded-lg shadow-sm transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!userInputs || (!user?.isPro && generatedNames.length === 0)}
                >
                <PdfIcon />
                Download PDF
                </button>
            </div>
            <NameList names={generatedNames} onToggleFavorite={handleToggleFavorite} />
            </div>
        )}

        <div className="mt-16 bg-gradient-to-br from-indigo-50 to-white rounded-2xl p-8 md:p-12 border border-indigo-100 text-center shadow-sm">
          <h2 className="text-2xl md:text-3xl font-bold text-indigo-900 mb-4">Ensure Your Brand Name is Flawless</h2>
          <p className="text-indigo-700 mb-8 max-w-2xl mx-auto text-lg">
            Generating a name is just the first step. Run it through our professional Brand Audit engine to analyze its phonetic clarity, digital readiness, and linguistic safety across global markets.
          </p>
          <button
            onClick={() => navigate('/brand-audit/')}
            className="inline-block px-8 py-4 bg-[#4F46E5] hover:bg-[#4338CA] text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
          >
            Start Your Free Brand Audit
          </button>
        </div>
      </div>
    )}
    </>
  );
};

export default App;
